import { useState } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';
import { Checkbox } from './checkbox';
import { Badge } from './badge';
import { 
  Download, 
  Trash2, 
  Filter, 
  X, 
  Calendar as CalendarIcon,
  CheckSquare,
  Square
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

/**
 * BulkActions Component
 * Provides bulk selection, actions (delete, export), and advanced filtering
 */
export function BulkActions({
  items = [],
  selectedIds = [],
  onSelectionChange,
  onBulkDelete,
  onExport,
  filters = [],
  activeFilters = {},
  onFilterChange,
  entityName = 'items',
}) {
  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < items.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(items.map(item => item.id));
    }
  };

  const activeFilterCount = Object.values(activeFilters).filter(v => v && v !== 'all').length;

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-muted/50 rounded-lg mb-4">
      {/* Select All Checkbox */}
      <div className="flex items-center gap-2">
        <Checkbox
          checked={allSelected}
          onCheckedChange={toggleSelectAll}
          className={someSelected ? 'opacity-50' : ''}
        />
        <span className="text-sm text-muted-foreground">
          {selectedIds.length > 0 
            ? `${selectedIds.length} of ${items.length} selected`
            : `Select all ${items.length} ${entityName}`
          }
        </span>
      </div>

      <div className="flex-1" />

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-2">
          {onBulkDelete && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onBulkDelete(selectedIds)}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete ({selectedIds.length})
            </Button>
          )}
          {onExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport(selectedIds)}
              data-testid="bulk-export-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              Export ({selectedIds.length})
            </Button>
          )}
        </div>
      )}

      {/* Export All */}
      {onExport && selectedIds.length === 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onExport(items.map(i => i.id))}
          data-testid="export-all-btn"
        >
          <Download className="h-4 w-4 mr-1" />
          Export All
        </Button>
      )}

      {/* Advanced Filters */}
      {filters.length > 0 && (
        <AdvancedFilters
          filters={filters}
          activeFilters={activeFilters}
          onFilterChange={onFilterChange}
          activeFilterCount={activeFilterCount}
        />
      )}
    </div>
  );
}

/**
 * Advanced Filters Popover
 */
function AdvancedFilters({ filters, activeFilters, onFilterChange, activeFilterCount }) {
  const [open, setOpen] = useState(false);

  const clearFilters = () => {
    const clearedFilters = {};
    filters.forEach(f => {
      clearedFilters[f.key] = f.type === 'select' ? 'all' : '';
    });
    onFilterChange(clearedFilters);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="relative" data-testid="advanced-filters-btn">
          <Filter className="h-4 w-4 mr-1" />
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Advanced Filters</h4>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-3 w-3 mr-1" />
                Clear all
              </Button>
            )}
          </div>

          {filters.map((filter) => (
            <div key={filter.key} className="space-y-2">
              <label className="text-sm font-medium">{filter.label}</label>
              
              {filter.type === 'select' && (
                <Select
                  value={activeFilters[filter.key] || 'all'}
                  onValueChange={(value) => onFilterChange({ ...activeFilters, [filter.key]: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={`Select ${filter.label}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {filter.options.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {filter.type === 'text' && (
                <Input
                  placeholder={filter.placeholder || `Search ${filter.label}...`}
                  value={activeFilters[filter.key] || ''}
                  onChange={(e) => onFilterChange({ ...activeFilters, [filter.key]: e.target.value })}
                />
              )}

              {filter.type === 'dateRange' && (
                <DateRangePicker
                  startDate={activeFilters[`${filter.key}_start`]}
                  endDate={activeFilters[`${filter.key}_end`]}
                  onStartChange={(date) => onFilterChange({ ...activeFilters, [`${filter.key}_start`]: date })}
                  onEndChange={(date) => onFilterChange({ ...activeFilters, [`${filter.key}_end`]: date })}
                />
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Date Range Picker
 */
function DateRangePicker({ startDate, endDate, onStartChange, onEndChange }) {
  return (
    <div className="flex gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "w-full justify-start text-left font-normal",
              !startDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {startDate ? format(startDate, "MMM d, yyyy") : "Start date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={startDate}
            onSelect={onStartChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "w-full justify-start text-left font-normal",
              !endDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {endDate ? format(endDate, "MMM d, yyyy") : "End date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={endDate}
            onSelect={onEndChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Selectable Row Checkbox
 */
export function SelectableRow({ id, selectedIds, onSelectionChange, children }) {
  const isSelected = selectedIds.includes(id);

  const toggleSelection = () => {
    if (isSelected) {
      onSelectionChange(selectedIds.filter(i => i !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  return (
    <tr className={cn(
      "border-b transition-colors",
      isSelected && "bg-primary/5"
    )}>
      <td className="p-2 w-10">
        <Checkbox
          checked={isSelected}
          onCheckedChange={toggleSelection}
          data-testid={`select-row-${id}`}
        />
      </td>
      {children}
    </tr>
  );
}

export default BulkActions;
